import React, { useState, ReactNode, useEffect } from 'react';

import {
    RootContainer,
    HintExpanderLabelWrapper,
    HintExpanderLabelIcon,
    HintExpanderLabelText,
    HintExpanderContent,
    TextTypeHint,
} from './styles';
import sanitizeHtml from '~/utils/sanitize-html';

interface HintExpanderProps {
    id: string;
    label: string | undefined;
    content?: ReactNode | string;
    type: 'text' | 'image' | 'html';
    isExpanded?: boolean; // this is the default state for hint expander, does not track manual action by user
    className?: string;
}

const HintExpander = ({
    id,
    label,
    content,
    type,
    className,
    isExpanded = false,
}: HintExpanderProps) => {
    const [activeExpander, setActiveExpander] = useState(isExpanded);
    const [userLastAction, setUserLastAction] = useState<'none' | 'open' | 'close'>('none');

    useEffect(() => {
        // If user hasn't interacted yet, follow isExpanded prop
        if (userLastAction === 'none') {
            setActiveExpander(isExpanded);
            return;
        }

        if (isExpanded) {
            // If user has interacted and isExpanded is true, open it
            setActiveExpander(true);
        } else if (userLastAction === 'close') {
            // If user has interacted, isExpanded is false, and the prev state is closed, close it
            setActiveExpander(false);
        }
    }, [isExpanded, userLastAction]);

    const activateExpander = () => {
        const newState = !activeExpander;
        setActiveExpander(newState);
        setUserLastAction(newState ? 'open' : 'close');
    };

    return (
        <RootContainer data-id="hint-expander" className={className}>
            <HintExpanderLabelWrapper
                type="button"
                id={`hint-expander-label-${id}`}
                aria-expanded={activeExpander}
                aria-controls={`hint-expander-content-${id}`}
                onClick={activateExpander}
            >
                <HintExpanderLabelIcon>
                    {activeExpander ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
                            <path
                                d="M7.4 15.4l4.6-4.58 4.6 4.58L18 14l-6-6-6 6 1.4 1.4z"
                                fill="#000"
                            />
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
                            <path
                                d="M7.4 8.6l4.6 4.58 4.6-4.58L18 10l-6 6-6-6 1.4-1.4z"
                                fill="#000"
                            />
                        </svg>
                    )}
                </HintExpanderLabelIcon>
                <HintExpanderLabelText>{label}</HintExpanderLabelText>
            </HintExpanderLabelWrapper>
            <HintExpanderContent
                aria-hidden={!activeExpander}
                id={`hint-expander-content-${id}`}
                aria-labelledby={`hint-expander-label-${id}`}
            >
                {type === 'text' ? (
                    <TextTypeHint>{content}</TextTypeHint>
                ) : type === 'html' ? (
                    <TextTypeHint
                        dangerouslySetInnerHTML={{
                            __html:
                                typeof content === 'string'
                                    ? sanitizeHtml(content, {
                                          FORCE_BODY: true,
                                          ALLOWED_ATTR: ['href', 'target'],
                                      })
                                    : '',
                        }}
                    />
                ) : (
                    content
                )}
            </HintExpanderContent>
        </RootContainer>
    );
};

export default HintExpander;
